'use client';

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import useSWR from 'swr';
import { getTeamChannel } from '@/lib/ws-client';
import { DockedChat } from './FloatingChatContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Minus, X, ExternalLink, Send, Loader2, ShoppingBag, Search, Sparkles, Video, PhoneCall } from 'lucide-react';
import { toast } from 'sonner';
import { parseSyslog } from '@/lib/syslog';
import { WhatsAppFormattedText } from './WhatsAppFormattedText';
import { encryptChatToken, maskPhone } from '@/lib/chat-encryption';
import { playAlertRing } from '@/lib/sound';

const fetcher = (url: string) => fetch(url).then((res) => res.json());

interface MessageItem {
  id: string;
  text: string | null;
  fromMe: boolean;
  timestamp: string;
  status?: string | null;
  mediaUrl?: string | null;
  mediaType?: string | null;
}

interface ProductItem {
  id: number;
  name: string;
  price: number | string;
  currency?: string;
  imageUrl?: string | null;
  coverImageUrl?: string | null;
  images?: string[];
  isActive?: boolean;
}

function areJidsEqual(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  const cleanA = a.replace(/:.*@/, '@').toLowerCase().trim();
  const cleanB = b.replace(/:.*@/, '@').toLowerCase().trim();
  if (cleanA === cleanB) return true;
  const numA = a.split('@')[0].replace(/\D/g, '');
  const numB = b.split('@')[0].replace(/\D/g, '');
  if (numA && numB && numA === numB) return true;
  return false;
}

export function FloatingChatBox({
  chat,
  onClose,
  onToggleMinimize,
}: {
  chat: DockedChat;
  onClose: () => void;
  onToggleMinimize: () => void;
}) {
  const router = useRouter();
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [displayName, setDisplayName] = useState(chat.name);
  const [isProductPopoverOpen, setIsProductPopoverOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDisplayName(chat.name);
  }, [chat.name]);

  const { data: team } = useSWR('/api/team', fetcher);
  const { data: user } = useSWR('/api/user', fetcher);
  const teamId = team?.id;

  const { data: membership } = useSWR<any>('/api/team/membership', fetcher);
  const isOwner = membership?.role === 'owner';
  const isAdmin = membership?.role === 'admin';
  const canShowContactInfo = isOwner || isAdmin || membership?.permissions?.showContactInfo === true;

  const isPhoneLike = (str: string) => /^\+?[\d\s\-().]{7,}$/.test(str.trim());
  const effectiveName = displayName || chat.name;
  const shownName = !canShowContactInfo && isPhoneLike(effectiveName) ? maskPhone(effectiveName) : effectiveName;
  const shownSub = !canShowContactInfo ? maskPhone(chat.remoteJid.split('@')[0]) : chat.remoteJid.split('@')[0];

  const messagesUrl = chat.id
    ? `/api/messages?chatId=${chat.id}&jid=${encodeURIComponent(chat.remoteJid)}`
    : `/api/messages?jid=${encodeURIComponent(chat.remoteJid)}`;

  const {
    data: messages,
    mutate: mutateMessages,
    isLoading,
  } = useSWR<MessageItem[]>(messagesUrl, fetcher, {
    revalidateOnFocus: true,
  });

  // Real-time CC Call Verification status listener
  const [liveCallStatus, setLiveCallStatus] = useState<string | null>(
    chat.callApprovalStatus || null
  );

  useEffect(() => {
    if (chat.callApprovalStatus) {
      setLiveCallStatus(chat.callApprovalStatus);
    }
  }, [chat.callApprovalStatus]);

  useEffect(() => {
    if (!teamId) return;
    const channel = getTeamChannel(teamId);
    if (!channel) return;

    const handleCallStatus = (data: any) => {
      if (data?.chatId && (data.chatId === chat.id || String(data.chatId) === String(chat.id))) {
        setLiveCallStatus(data.status || null);
      }
    };

    channel.bind('chat-call-status-changed', handleCallStatus);
    return () => {
      channel.unbind('chat-call-status-changed', handleCallStatus);
    };
  }, [teamId, chat.id]);

  // Fetch active store products for the popover
  const { data: products = [], isLoading: isLoadingProducts } = useSWR<ProductItem[]>(
    isProductPopoverOpen ? '/api/ecommerce/products?active=true' : null,
    fetcher
  );

  const filteredProducts = useMemo(() => {
    if (!products || !Array.isArray(products)) return [];
    if (!productSearch.trim()) return products;
    const query = productSearch.toLowerCase().trim();
    return products.filter((p) => p.name.toLowerCase().includes(query));
  }, [products, productSearch]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (messages && messages.length > 0) {
      scrollToBottom();
    }
  }, [messages, scrollToBottom]);

  // Auto-focus input when opened/unminimized so typing can start immediately
  useEffect(() => {
    if (!chat.isMinimized) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 120);
      return () => clearTimeout(timer);
    }
  }, [chat.isMinimized]);

  // Mark messages as read as soon as the chat is docked (opened), even if minimized
  useEffect(() => {
    if (chat.id) {
      fetch('/api/chats/mark-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId: chat.id, remoteJid: chat.remoteJid }),
      }).catch(() => {});
    }
  }, [chat.id, chat.remoteJid]);

  // Real-time WebSocket updates for this chat
  useEffect(() => {
    if (!teamId) return;
    const channel = getTeamChannel(teamId);
    if (!channel) return;

    const handleNewMessage = (payload: any) => {
      const matchJid = areJidsEqual(payload.remoteJid, chat.remoteJid);
      const matchChatId = payload.chatId && chat.id && payload.chatId === chat.id;

      if (matchJid || matchChatId) {
        if (payload.name) setDisplayName(payload.name);
        mutateMessages((curr = []) => {
          if (curr.some((m) => m.id === payload.id)) return curr;

          const textContent = payload.text || payload.caption || payload.mediaCaption || (payload.mediaUrl ? 'Attachment' : '');

          if (payload.fromMe) {
            const tempIdx = curr.findIndex((m) => m.id.startsWith('temp_'));
            if (tempIdx > -1) {
              const updated = [...curr];
              updated[tempIdx] = {
                id: payload.id,
                text: textContent,
                fromMe: true,
                timestamp: payload.timestamp || new Date().toISOString(),
                status: payload.status || 'sent',
                mediaUrl: payload.mediaUrl,
                mediaType: payload.mediaType || payload.messageType,
              };
              return updated;
            }
          }

          return [
            ...curr,
            {
              id: payload.id || `msg_${Date.now()}`,
              text: textContent,
              fromMe: payload.fromMe,
              timestamp: payload.timestamp || new Date().toISOString(),
              status: payload.status,
              mediaUrl: payload.mediaUrl,
              mediaType: payload.mediaType || payload.messageType,
            },
          ];
        }, false);
        if (payload.fromMe === false) {
          playAlertRing();
        }
        setTimeout(scrollToBottom, 50);
      }
    };

    const handleContactUpdate = (payload: any) => {
      if (payload?.name && (areJidsEqual(payload.remoteJid, chat.remoteJid) || (payload.chatId && chat.id && payload.chatId === chat.id))) {
        setDisplayName(payload.name);
      }
    };

    channel.bind('new-message', handleNewMessage);
    channel.bind('contact-update', handleContactUpdate);
    return () => {
      channel.unbind('new-message', handleNewMessage);
      channel.unbind('contact-update', handleContactUpdate);
    };
  }, [teamId, chat.remoteJid, chat.id, mutateMessages, scrollToBottom]);

  // Dispatch products catalog & cart link for chat
  const sendProductsMenu = async (page: number = 1) => {
    const tempId = `temp_menu_${Date.now()}`;
    const optimisticMessage: MessageItem = {
      id: tempId,
      text: `🛍️ Generating product catalog & cart link for chat...`,
      fromMe: true,
      timestamp: new Date().toISOString(),
      status: 'sending',
    };
    mutateMessages((curr = []) => [...curr, optimisticMessage], false);
    setTimeout(scrollToBottom, 50);

    try {
      const res = await fetch('/api/ecommerce/send-menu', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipientJid: chat.remoteJid,
          instanceId: chat.instanceId,
          chatId: chat.id,
          page,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || 'Failed to send store link');
      } else {
        toast.success('Store & Cart link sent to customer on WhatsApp!');
      }
      mutateMessages();
    } catch {
      toast.error('Network error sending store link');
      mutateMessages();
    } finally {
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  };

  // Dispatch single product detail
  const sendSingleProduct = async (productId: number) => {
    const tempId = `temp_prod_${Date.now()}`;
    const optimisticMessage: MessageItem = {
      id: tempId,
      text: `🛍️ Sending product #${productId}...`,
      fromMe: true,
      timestamp: new Date().toISOString(),
      status: 'sending',
    };
    mutateMessages((curr = []) => [...curr, optimisticMessage], false);
    setTimeout(scrollToBottom, 50);

    try {
      const res = await fetch('/api/ecommerce/send-product', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipientJid: chat.remoteJid,
          productId,
          instanceId: chat.instanceId,
          chatId: chat.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || `Failed to send product #${productId}`);
      } else {
        toast.success(data.productName ? `Product "${data.productName}" sent!` : `Product #${productId} sent!`);
      }
      mutateMessages();
    } catch {
      toast.error('Network error sending product');
      mutateMessages();
    } finally {
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  };

  // Dispatch call invitation card
  const sendCallCard = async () => {
    const tempId = `temp_call_${Date.now()}`;
    const optimisticMessage: MessageItem = {
      id: tempId,
      text: '📹 Sending video call invitation...',
      fromMe: true,
      timestamp: new Date().toISOString(),
      status: 'sending',
    };
    mutateMessages((curr = []) => [...curr, optimisticMessage], false);
    setTimeout(scrollToBottom, 50);

    try {
      const res = await fetch('/api/calls/send-card', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipientJid: chat.remoteJid,
          instanceId: chat.instanceId,
          chatId: chat.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || 'Failed to send call invitation');
      } else {
        toast.success('Video call invitation sent!');
      }
      mutateMessages();
    } catch {
      toast.error('Network error sending call invitation');
      mutateMessages();
    } finally {
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  };

  const handleSend = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = inputText.trim();
    if (!trimmed || isSending) return;

    // Intercept /call command
    if (trimmed.toLowerCase().startsWith('/call')) {
      setInputText('');
      setIsSending(true);
      await sendCallCard();
      setIsSending(false);
      return;
    }

    // Intercept /product and /products commands
    if (trimmed.toLowerCase().startsWith('/product')) {
      const singleMatch = trimmed.match(/^\/product\s+(?:#|id=)?(\d+)/i);
      setInputText('');
      setIsSending(true);

      if (singleMatch) {
        const prodId = parseInt(singleMatch[1], 10);
        await sendSingleProduct(prodId);
        setIsSending(false);
        return;
      }

      // Catalog menu command (/product or /products or /products 2)
      const match = trimmed.match(/^\/products?(?:\s+(\d+)|(?:\?page=|\s+page=)(\d+))?/i);
      const pageNum = match ? parseInt(match[1] || match[2] || '1', 10) : 1;
      await sendProductsMenu(pageNum);
      setIsSending(false);
      return;
    }

    const tempId = `temp_${Date.now()}`;
    const optimisticMessage: MessageItem = {
      id: tempId,
      text: trimmed,
      fromMe: true,
      timestamp: new Date().toISOString(),
      status: 'sending',
    };

    // Optimistic UI update
    mutateMessages((curr = []) => [...curr, optimisticMessage], false);
    setInputText('');
    setIsSending(true);

    // Maintain focus for fast chat!
    inputRef.current?.focus();

    try {
      const res = await fetch('/api/messages/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipientJid: chat.remoteJid,
          text: trimmed,
          instanceId: chat.instanceId,
          agentName: user?.name || undefined,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        toast.error(err.error || 'Failed to send message');
        mutateMessages();
      } else {
        setTimeout(scrollToBottom, 50);
      }
    } catch {
      toast.error('Network error sending message');
      mutateMessages();
    } finally {
      setIsSending(false);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  };

  const handlePopout = () => {
    const isWebChat = chat.remoteJid?.endsWith('@web.chat') || chat.remoteJid?.startsWith('web_');
    const isFbChat = chat.remoteJid?.endsWith('@facebook.messenger') || chat.remoteJid?.startsWith('fb_');
    const isIgChat = chat.remoteJid?.endsWith('@instagram.messenger') || chat.remoteJid?.endsWith('@instagram.direct') || chat.remoteJid?.startsWith('ig_');
    const cleanJid = (chat.remoteJid?.endsWith('@g.us') || isWebChat || isFbChat || isIgChat)
      ? chat.remoteJid
      : chat.remoteJid?.split('@')[0];

    router.push(`/dashboard/chat/${encryptChatToken(cleanJid || '', chat.instanceId)}`);
  };

  // Minimized Mode (Facebook-style bottom tab)
  if (chat.isMinimized) {
    return (
      <div
        onClick={onToggleMinimize}
        className="w-52 h-10 bg-background border border-border shadow-xl rounded-t-xl px-3 flex items-center justify-between cursor-pointer hover:bg-muted/80 transition-colors pointer-events-auto"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="relative">
            <Avatar className="h-6 w-6">
              <AvatarImage src={chat.avatar || ''} />
              <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                {shownName.substring(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-500 ring-1 ring-background" />
          </div>
          <span className="font-semibold text-xs truncate">{shownName}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="text-muted-foreground hover:text-foreground p-0.5 rounded"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  const showProductCommandHint = inputText.startsWith('/') && ('product'.startsWith(inputText.slice(1).toLowerCase()) || 'products'.startsWith(inputText.slice(1).toLowerCase()));
  const showCallCommandHint = inputText.startsWith('/') && ('call'.startsWith(inputText.slice(1).toLowerCase()) || 'calls'.startsWith(inputText.slice(1).toLowerCase()) || 'video'.startsWith(inputText.slice(1).toLowerCase()));

  // Expanded Window
  return (
    <div className="w-80 h-[430px] bg-background border border-border shadow-2xl rounded-t-2xl flex flex-col pointer-events-auto overflow-hidden animate-in slide-in-from-bottom-5 duration-200">
      {/* Top Header */}
      <div
        className="h-12 bg-primary/10 border-b border-border px-3 flex items-center justify-between cursor-pointer select-none shrink-0"
        onClick={onToggleMinimize}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="relative">
            <Avatar className="h-7 w-7">
              <AvatarImage src={chat.avatar || ''} />
              <AvatarFallback className="text-xs bg-primary/20 text-primary font-semibold">
                {shownName.substring(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-500 ring-1 ring-background" />
          </div>
          <div className="min-w-0 flex flex-col">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-xs truncate text-foreground leading-tight">
                {shownName}
              </span>
              {liveCallStatus === 'rejected' && (
                <span
                  title="Call verification rejected by CC WhatsApp"
                  className="text-[9px] px-1.5 py-0.2 rounded font-semibold bg-rose-500/15 text-rose-700 dark:text-rose-400 border border-rose-500/30 shrink-0"
                >
                  Rejected by Cc whatsapp
                </span>
              )}
              {liveCallStatus === 'approved' && (
                <span
                  title="Call verification approved by CC WhatsApp"
                  className="text-[9px] px-1.5 py-0.2 rounded font-semibold bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 shrink-0"
                >
                  Approved
                </span>
              )}
            </div>
            <span className="text-[10px] text-muted-foreground truncate">
              {shownSub}
            </span>
          </div>
        </div>

        {/* Window controls */}
        <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="icon"
            onClick={handlePopout}
            title="Open full page chat"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleMinimize}
            title="Minimize"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <Minus className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            title="Close"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* CC Call Verification Alert Banner */}
      {liveCallStatus === 'rejected' && (
        <div className="bg-rose-500/10 border-b border-rose-500/20 px-3 py-1.5 flex items-center justify-between text-[11px] text-rose-700 dark:text-rose-400 font-semibold shrink-0">
          <div className="flex items-center gap-1.5">
            <X className="h-3.5 w-3.5 text-rose-600 dark:text-rose-400 shrink-0" />
            <span>Rejected by Cc whatsapp</span>
          </div>
        </div>
      )}

      {/* Message Feed */}
      <div className="flex-1 p-3 overflow-y-auto space-y-2 text-xs bg-muted/20">
        {isLoading && (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}

        {messages?.map((msg) => {
          const isFromMe = msg.fromMe;
          const time = msg.timestamp
            ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : '';

          // Centered badge for system events (e.g. @@syslog_moved_to_stage)
          const syslog = parseSyslog(msg.text);
          if (syslog.isSyslog) {
            return (
              <div key={msg.id} className="flex justify-center my-1.5 w-full">
                <div className="bg-muted/80 border border-border/80 text-foreground text-[10.5px] px-3 py-1 rounded-full shadow-2xs max-w-[92%] text-center font-medium flex items-center gap-1.5 justify-center">
                  <span className="text-muted-foreground text-xs">📌</span>
                  <span>{syslog.text}</span>
                  {time && <span className="ml-1 text-[9px] text-muted-foreground">{time}</span>}
                </div>
              </div>
            );
          }

          return (
            <div
              key={msg.id}
              className={`flex flex-col ${isFromMe ? 'items-end' : 'items-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-3 py-1.5 break-words shadow-xs text-black ${
                  isFromMe
                    ? 'bg-[#d9fdd3] rounded-br-xs'
                    : 'bg-card border border-border rounded-bl-xs'
                }`}
                style={{ color: '#000000' }}
              >
                {msg.mediaUrl && (
                  <div className="rounded overflow-hidden mb-1">
                    <img
                      src={msg.mediaUrl}
                      alt="Attachment"
                      className="max-w-full max-h-48 object-cover rounded cursor-pointer"
                      onClick={() => window.open(msg.mediaUrl!, '_blank')}
                      onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                    />
                  </div>
                )}
                {msg.text && (!msg.mediaUrl || !['image message', 'video message', '📷 imagem', '📷 image', '📄 documento', 'media'].includes(msg.text.trim().toLowerCase())) && (
                  <p className="whitespace-pre-wrap text-black dark:text-white" style={{ color: '#000000' }}>
                    <WhatsAppFormattedText text={msg.text} />
                  </p>
                )}
                <div
                  className="text-[9px] mt-0.5 text-right text-black/60"
                  style={{ color: '#4a5568' }}
                >
                  {time}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Autocomplete command hint popup */}
      {(showProductCommandHint || showCallCommandHint) && (
        <div className="px-2 py-1 bg-background border-t border-border flex items-center gap-1.5 flex-wrap">
          {showCallCommandHint && (
            <button
              type="button"
              onClick={() => {
                sendCallCard();
                setInputText('');
                inputRef.current?.focus();
              }}
              className="text-[11px] px-2 py-1 rounded bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 font-medium flex items-center gap-1.5 transition-colors"
            >
              <Video className="h-3 w-3" />
              <span><strong>/call</strong> — Share Video Call Card</span>
            </button>
          )}
          {showProductCommandHint && (
            <button
              type="button"
              onClick={() => {
                setInputText('/product');
                inputRef.current?.focus();
              }}
              className="text-[11px] px-2 py-1 rounded bg-rose-500/10 text-rose-600 hover:bg-rose-500/20 font-medium flex items-center gap-1.5 transition-colors"
            >
              <ShoppingBag className="h-3 w-3" />
              <span><strong>/product</strong> — Send Product Catalog Menu</span>
            </button>
          )}
        </div>
      )}

      {/* Chat Input Bar */}
      <form
        onSubmit={handleSend}
        className="p-2 border-t border-border bg-background flex items-center gap-1.5 shrink-0"
      >
        {/* Quick Call Button */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          title="Share Video Call Card (/call)"
          onClick={sendCallCard}
          className="h-8 w-8 rounded-full text-emerald-500 hover:text-emerald-600 hover:bg-emerald-500/10 shrink-0"
        >
          <Video className="h-4 w-4" />
        </Button>

        {/* Quick Product Popover Button */}
        <Popover open={isProductPopoverOpen} onOpenChange={setIsProductPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              title="Product Catalog & Items"
              className="h-8 w-8 rounded-full text-rose-500 hover:text-rose-600 hover:bg-rose-500/10 shrink-0"
            >
              <ShoppingBag className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" className="w-72 p-3 shadow-2xl rounded-xl">
            <div className="flex items-center justify-between pb-2 border-b border-border">
              <span className="font-semibold text-xs flex items-center gap-1.5">
                <ShoppingBag className="h-3.5 w-3.5 text-rose-500" />
                Product Catalog
              </span>
              <Button
                size="sm"
                variant="default"
                className="h-6 text-[10px] px-2 bg-rose-600 hover:bg-rose-700 text-white rounded-md"
                onClick={() => {
                  setIsProductPopoverOpen(false);
                  sendProductsMenu(1);
                }}
              >
                Send Menu
              </Button>
            </div>

            {/* Product Search */}
            <div className="relative mt-2">
              <Search className="absolute left-2 top-2 h-3 w-3 text-muted-foreground" />
              <Input
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Search products..."
                className="h-7 text-xs pl-7 pr-2"
              />
            </div>

            {/* Products List */}
            <div className="max-h-48 overflow-y-auto space-y-1 mt-2 pr-0.5">
              {isLoadingProducts ? (
                <div className="py-4 text-center text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="py-3 text-center text-xs text-muted-foreground">
                  No active products found
                </div>
              ) : (
                filteredProducts.map((prod) => (
                  <div
                    key={prod.id}
                    className="flex items-center justify-between p-1.5 hover:bg-muted/60 rounded-lg transition-colors gap-2 text-xs"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate text-[11px] text-foreground leading-tight">
                        {prod.name}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {prod.currency || 'USD'} {prod.price}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-[10px] px-2 shrink-0 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40"
                      onClick={() => {
                        setIsProductPopoverOpen(false);
                        sendSingleProduct(prod.id);
                      }}
                    >
                      Send
                    </Button>
                  </div>
                ))
              )}
            </div>
          </PopoverContent>
        </Popover>

        <Input
          ref={inputRef}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder="Type a message or /product..."
          className="h-8 text-xs bg-muted/50 border-border focus-visible:ring-1 focus-visible:ring-primary rounded-full px-3 flex-1 min-w-0"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <Button
          type="submit"
          size="icon"
          disabled={!inputText.trim() || isSending}
          className="h-8 w-8 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shrink-0"
        >
          {isSending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
        </Button>
      </form>
    </div>
  );
}

export default FloatingChatBox;
